import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Lock, Bot, ShieldCheck, MessageCircle } from "lucide-react";

export default function Contact() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (user) api.get("/settings/contact").then((r) => setInfo(r.data)).catch(() => {});
  }, [user]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="contact-page">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">Get in touch</p>
      <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-white">Contact us</h1>
      <p className="text-slate-300 mt-4 leading-relaxed">
        Our direct email address and phone number are reserved for <span className="text-white font-semibold">signed-in members</span>.
        This keeps our support focused on registered learners. If you're not signed in yet, our AI assistant is available 24/7 to
        answer questions about courses, exams, payments, and policies.
      </p>

      {ready && user ? (
        <div className="mt-10 glass rounded-2xl p-8" data-testid="contact-details">
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold mb-6">
            <ShieldCheck className="h-4 w-4" /> Signed in as {user.email}
          </div>
          <div className="space-y-5">
            <a href={`mailto:${info?.contact_email || ""}`} className="flex items-center gap-4 bg-surface-2 rounded-xl p-5 border border-white/10 hover:border-gold/40 transition-colors" data-testid="contact-email">
              <div className="h-11 w-11 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center"><Mail className="h-5 w-5 text-gold" /></div>
              <div><p className="text-xs text-slate-400">Email</p><p className="text-white font-semibold">{info?.contact_email || "…"}</p></div>
            </a>
            <a href={`https://wa.me/${(info?.contact_phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 bg-surface-2 rounded-xl p-5 border border-white/10 hover:border-gold/40 transition-colors" data-testid="contact-phone">
              <div className="h-11 w-11 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center"><MessageCircle className="h-5 w-5 text-gold" /></div>
              <div><p className="text-xs text-slate-400">WhatsApp</p><p className="text-white font-semibold">{info?.contact_phone || "…"}</p></div>
            </a>
          </div>
        </div>
      ) : (
        <div className="mt-10 glass rounded-2xl p-8 text-center" data-testid="contact-locked">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-gold/15 border border-gold/30 items-center justify-center mb-4"><Lock className="h-7 w-7 text-gold" /></div>
          <h2 className="font-display text-xl font-bold text-white">Contact details are members-only</h2>
          <p className="text-slate-400 mt-2 text-sm max-w-md mx-auto">
            Sign in or create a free account to see our direct email and phone number. In the meantime, our AI assistant can help right away.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button className="btn-gold" onClick={() => nav("/login")} data-testid="contact-login-btn">Sign in</Button>
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => nav("/register")} data-testid="contact-register-btn">Create account</Button>
          </div>
          <p className="text-xs text-slate-500 mt-6 flex items-center justify-center gap-2"><Bot className="h-4 w-4 text-gold" />Use the AI assistant in the bottom-right corner anytime.</p>
        </div>
      )}
    </div>
  );
}
